import { Box } from '@mantine/core';
import { useWindowScroll, useViewportSize } from '@mantine/hooks';
import { forwardRef, useRef, useState, useEffect, useMemo } from 'react';
import { useReducedMotion } from '../hooks';

export interface ParallaxProps {
  children: React.ReactNode;
  speed?: number; // -1 to 1 (-1 = opposite direction, 1 = same direction faster)
  direction?: 'vertical' | 'horizontal';
  disabled?: boolean;
  overflow?: 'visible' | 'hidden';
}

const Parallax = forwardRef<HTMLDivElement, ParallaxProps>(
  (
    {
      children,
      speed = 0.5,
      direction = 'vertical',
      disabled = false,
      overflow = 'hidden',
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const containerRef = useRef<HTMLDivElement>(null);
    const [{ y: scrollY }] = useWindowScroll();
    const { height: viewportHeight } = useViewportSize();
    const [elementTop, setElementTop] = useState(0);

    useEffect(() => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setElementTop(rect.top + scrollY);
      }
    }, [scrollY]);

    const transform = useMemo(() => {
      if (reducedMotion || disabled) return 'none';

      const relativeScroll = scrollY - elementTop + viewportHeight;
      const offset = relativeScroll * speed * 0.1;

      if (direction === 'horizontal') {
        return `translate3d(${offset}px, 0, 0)`;
      }
      return `translate3d(0, ${offset}px, 0)`;
    }, [scrollY, elementTop, viewportHeight, speed, direction, reducedMotion, disabled]);

    return (
      <Box
        ref={containerRef}
        style={{
          overflow,
          position: 'relative',
        }}
      >
        <Box
          ref={ref}
          style={{
            transform,
            willChange: reducedMotion || disabled ? 'auto' : 'transform',
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }
);

Parallax.displayName = 'Parallax';

export default Parallax;
